import { useState, useMemo } from "react";
import {
  queryPresets,
  applyPresetToProfile,
  type GalleryPreset,
  type ApplyMode,
  type PresetUndoSnapshot,
} from "@profolio/templates";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import { TemplateCard } from "./TemplateCard";
import { TemplateFilterBar, type FilterState } from "./TemplateFilterBar";
import { TemplatePreviewModal } from "./TemplatePreviewModal";
import { TemplateApplyModal } from "./TemplateApplyModal";
import { toast } from "sonner";
import { Undo2, ArrowLeft, Layers, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TemplateGalleryViewProps {
  onBackToEditor?: () => void;
  className?: string;
}

export function TemplateGalleryView({
  onBackToEditor,
  className = "",
}: TemplateGalleryViewProps) {
  const { draft, dispatch, userTier } = useProfileDraft();

  // Filter State
  const [filters, setFilters] = useState<FilterState>({
    category: "all",
    search: "",
    purpose: "all",
    aesthetic: "all",
    contentEmphasis: "all",
    tier: "all",
  });

  // Modal State
  const [previewPreset, setPreviewPreset] = useState<GalleryPreset | null>(null);
  const [applyPreset, setApplyPreset] = useState<GalleryPreset | null>(null);

  // Undo Snapshot State
  const [lastUndoSnapshot, setLastUndoSnapshot] = useState<PresetUndoSnapshot | null>(null);

  // Counts for Category Tabs
  const counts = useMemo(() => {
    return {
      all: queryPresets().length,
      faculty: queryPresets({ category: "faculty-teaching" }).length,
      research: queryPresets({ category: "research-publications" }).length,
      earlyCareer: queryPresets({ category: "early-career-phd" }).length,
    };
  }, []);

  // Filtered Presets
  const filteredPresets = useMemo(() => {
    return queryPresets({
      category: filters.category === "all" ? undefined : filters.category,
      search: filters.search.trim() ? filters.search : undefined,
      purpose: filters.purpose === "all" ? undefined : filters.purpose,
      aesthetic: filters.aesthetic === "all" ? undefined : filters.aesthetic,
      contentEmphasis:
        filters.contentEmphasis === "all" ? undefined : filters.contentEmphasis,
      tier: filters.tier === "all" ? undefined : filters.tier,
    });
  }, [filters]);

  const handleFilterChange = (partial: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  };

  const handleResetFilters = () => {
    setFilters({
      category: "all",
      search: "",
      purpose: "all",
      aesthetic: "all",
      contentEmphasis: "all",
      tier: "all",
    });
  };

  const handleApplyPreset = (options: {
    presetId: string;
    mode: ApplyMode;
    sectionsToAdd: string[];
  }) => {
    if (!draft) return;

    try {
      const result = applyPresetToProfile({
        presetId: options.presetId,
        targetTier: userTier,
        currentProfile: draft.profile,
        currentSections: draft.sections,
        mode: options.mode,
        sectionsToAdd: options.sectionsToAdd,
      });

      // Save undo snapshot
      setLastUndoSnapshot(result.undoSnapshot);

      // Dispatch atomic update to draft state
      dispatch({
        type: "APPLY_TEMPLATE_PRESET",
        payload: {
          profile: result.updatedProfile,
          sections: result.updatedSections,
          designPreset: options.presetId,
        },
      });

      const appliedPreset = queryPresets().find((p) => p.presetId === options.presetId);
      const presetName = appliedPreset?.name || options.presetId;

      toast.success(`Applied "${presetName}" to your draft!`, {
        description:
          options.mode === "visual-only"
            ? "Visual appearance updated. Content preserved."
            : "Design and section structure updated.",
        action: {
          label: "Undo",
          onClick: () => handleUndo(result.undoSnapshot),
        },
      });
    } catch (err) {
      console.error("Failed to apply preset:", err);
      toast.error("Failed to apply template preset.");
    }
  };

  const handleUndo = (snapshot?: PresetUndoSnapshot | null) => {
    const targetSnapshot = snapshot || lastUndoSnapshot;
    if (!targetSnapshot || !draft) return;

    dispatch({
      type: "APPLY_TEMPLATE_PRESET",
      payload: {
        profile: targetSnapshot.previousProfileState,
        sections: targetSnapshot.previousSectionsState,
        designPreset: targetSnapshot.previousProfileState.lastAppliedPresetId || "custom",
      },
    });

    setLastUndoSnapshot(null);
    toast.info("Template changes reverted.");
  };

  const existingSectionTypes = useMemo(() => {
    return (draft?.sections || []).map((s) => s.type);
  }, [draft?.sections]);

  const currentlyAppliedPresetId = draft?.profile.lastAppliedPresetId;

  return (
    <div className={`flex flex-col w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 ${className}`}>
      {/* ── Top Navigation Bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          {onBackToEditor && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onBackToEditor}
              className="text-xs font-semibold cursor-pointer"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />
              Back to Editor
            </Button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-foreground">
                Academic Template Gallery
              </h1>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                17 PRESETS
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Curated, art-directed website templates tailored specifically for academic researchers and educators.
            </p>
          </div>
        </div>

        {/* Undo notification banner if recently applied */}
        {lastUndoSnapshot && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-primary/20 bg-primary/5 text-xs text-foreground animate-in fade-in-50">
            <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
            <span>Preset applied to draft.</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleUndo()}
              className="text-xs h-7 text-primary hover:text-primary font-bold cursor-pointer"
            >
              <Undo2 className="h-3 w-3 mr-1" />
              Undo
            </Button>
          </div>
        )}
      </div>

      {/* ── Filter Bar ── */}
      <TemplateFilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        counts={counts}
      />

      {/* ── Presets Grid ── */}
      {filteredPresets.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center border border-dashed border-border rounded-2xl bg-card/40 my-8">
          <Layers className="h-10 w-10 text-muted-foreground/60 mb-3" />
          <h3 className="text-sm font-bold text-foreground">No templates match your filters</h3>
          <p className="text-xs text-muted-foreground max-w-sm mt-1 mb-4">
            Try adjusting your category, aesthetic, or search query to explore other academic designs.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleResetFilters}
            className="text-xs"
          >
            Clear All Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPresets.map((preset) => (
            <TemplateCard
              key={preset.presetId}
              preset={preset}
              onPreview={(p) => setPreviewPreset(p)}
              onApply={(p) => setApplyPreset(p)}
              isCurrentlyApplied={currentlyAppliedPresetId === preset.presetId}
            />
          ))}
        </div>
      )}

      {/* ── Live Preview Modal ── */}
      <TemplatePreviewModal
        preset={previewPreset}
        isOpen={!!previewPreset}
        onClose={() => setPreviewPreset(null)}
        onApply={(p) => {
          setPreviewPreset(null);
          setApplyPreset(p);
        }}
        userTier={userTier}
      />

      {/* ── Apply Confirmation Modal ── */}
      <TemplateApplyModal
        preset={applyPreset}
        isOpen={!!applyPreset}
        onClose={() => setApplyPreset(null)}
        onConfirmApply={handleApplyPreset}
        existingSectionTypes={existingSectionTypes}
        userTier={userTier}
      />
    </div>
  );
}
